/* eslint-disable no-console */
import Axios from 'axios';
import mixpanel from 'mixpanel-browser';
import { BehaviorSubject, merge, Observable, Observer, Subject } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { io, Socket } from 'socket.io-client';

import { NewRelic, newRelicNoticeError } from '../hooks/useNewRelic';
import {
  Game,
  GameSaveDTO,
  PartialParticipant,
  Participant,
  Response,
} from '../services/backendService/types';
import { configureStore } from '../utils/store';

class GameAPI {
  static instance: GameAPI;
  private _socket: Socket;
  private onReconnectFailedSub: Subject<void> = new Subject();
  private reconnectionAttemptsBeforeNotifying = 1;
  private hasPoorInternetConnection: BehaviorSubject<boolean>;
  private latencyBehaviorSubject: BehaviorSubject<number>;
  private timeToConnectStart: number | undefined;
  private initTimeToConnectStart: number;
  private hasFirstTimeToConnectBeenEmitted = false;
  constructor(token: string) {
    const poorConnectionBehaviorSubject = new BehaviorSubject(false);
    this.latencyBehaviorSubject = new BehaviorSubject<number>(0);
    this.hasPoorInternetConnection = poorConnectionBehaviorSubject;
    const { apiUrl } = configureStore().config.game;
    const socket = io(`${process.env.isLocal ? 'http://localhost:3011' : apiUrl}/hosts`, {
      auth: {
        token,
      },
      transports: ['websocket'],
      withCredentials: true,
      autoConnect: true,
    });
    this.initTimeToConnectStart = Date.now();

    console.log('connecting to sockets');

    socket.on('connect', () => {
      console.log('[ROOT] Socket successfully Connected');

      if (!this.hasFirstTimeToConnectBeenEmitted) {
        NewRelic()?.addPageAction('TimeToConnectionHost', {
          timeToConnection: Date.now() - this.initTimeToConnectStart,
          isReconnect: false,
        });
        this.hasFirstTimeToConnectBeenEmitted = true;
      }
      if (this.timeToConnectStart !== undefined) {
        NewRelic()?.addPageAction('TimeToConnectionHost', {
          timeToConnection: Date.now() - this.timeToConnectStart,
          isReconnect: true,
        });
      }
      this.hasPoorInternetConnection.next(false);
    });

    socket.on('connect_error', err => {
      if (err instanceof Error) {
        mixpanel.track('Game.ConnectError');
        newRelicNoticeError(err, 'connect_error');
        this.getRefreshToken()
          .then(token => {
            // Hard to handle types
            //@ts-ignore
            this._socket?.auth.token = token;
            this._socket?.connect();
            mixpanel.track('Game.Auth.Reconnected');
          })
          .catch(error => {
            mixpanel.track('Game.Auth.ReconnectionFailed');

            NewRelic()?.noticeError(error);
          });
      } else {
        newRelicNoticeError(err, 'connect_error', 'not instance of Error');
      }
    });

    socket.on('error', error => {
      console.error('Socket error', error);

      NewRelic()?.noticeError(error);
    });

    socket.io.on('reconnect_error', error => {
      console.error('reconnect_error', error);
      NewRelic()?.noticeError(error);
    });

    socket.io.on('reconnect_attempt', attempt => {
      console.log('Reconnecting', attempt);
      if (attempt > this.reconnectionAttemptsBeforeNotifying) {
        // every time we are reconnecting after a few attempts, we will notify the user about a bad internet connection
        // because we use cloud run on the backend, it is expected to have a few reconnection attempts before the client
        // is successfully reconnected
        if (this.hasPoorInternetConnection.getValue() === false) {
          this.hasPoorInternetConnection.next(true);
        }
      }

      if (attempt <= 1) {
        // Reset connect timer
        this.timeToConnectStart = Date.now();
      }
    });

    socket.on('disconnect', reason => {
      if (reason === 'io server disconnect') {
        // Manual reconnect if the socket gets disconnected
        console.log('io server disconnect', reason);
        // Manually reconnect
        socket.connect();
        return;
      }
    });
    this._socket = socket;
  }

  private getRefreshToken = async () => {
    const res = await Axios.get<{ id_token: string }>(
      `${window.location.origin}/api/auth/refreshToken`,
    );
    return res.data.id_token;
  };

  public getSocket() {
    if (!this._socket) throw Error('No socket exist');
    return this._socket;
  }

  public disconnect() {
    this._socket?.disconnect();
  }

  public ensureConnected() {
    const $connection = this.createEventObservable<void>('connect').pipe(
      tap(() => console.log('Successfully connected')),
    );
    const $reconnection = this.createEventObservable<void>('reconnect').pipe(
      tap(() => console.log('Successfully reconnected')),
    );

    const continueIfConnected = () => {
      if (this._socket?.connected) {
        return new Observable(subscriber => {
          subscriber.next();
        });
      } else {
        this._socket?.connect();
        return new Observable();
      }
    };
    return merge(continueIfConnected(), $connection, $reconnection);
  }

  public onReconnectFailed() {
    return this.onReconnectFailedSub.asObservable();
  }
  public $hasPoorInternet() {
    return this.hasPoorInternetConnection.asObservable();
  }

  public $latency() {
    return this.latencyBehaviorSubject.asObservable();
  }

  public emitLatency(latency: number) {
    this.latencyBehaviorSubject.next(latency);
  }

  public emitSlowInternet() {
    this.hasPoorInternetConnection.next(true);
  }

  public emitFastInternet() {
    this.hasPoorInternetConnection.next(false);
  }

  public getHasPoorInternetConnection() {
    return this.hasPoorInternetConnection.getValue();
  }

  public createEventObservable<T>(event: string): Observable<T> {
    return new Observable((incoming: Observer<T>) => {
      const callback = (data: T) => {
        incoming.next(data as T);
      };
      this._socket?.on(event, callback);
      return () => {
        this._socket?.off(event, callback);
      };
    });
  }

  gameResponse = {
    uploadUrl: async ({ fileId }: { fileId: string }) => {
      return new Promise<{ url: string }>((resolve, reject) => {
        this._socket?.emit(
          'game:uploadurl',
          { filename: fileId },
          (res: AcknowledgementResponse<{ url: string }>) => {
            if (res.error) {
              console.error(res.error);

              NewRelic()?.noticeError(res.error);
              reject(res.error);
              return;
            }
            if (!res.data) {
              reject();
              return;
            }
            resolve(res.data);
          },
        );
      });
    },
    downloadUrl: async ({ fileId }: { fileId: string }) => {
      return new Promise<{ url: string }>((resolve, reject) => {
        this._socket?.emit(
          'game:downloadurl',
          {
            filename: fileId,
          },
          (res: AcknowledgementResponse<{ url: string }>) => {
            if (res.error) {
              console.error(res.error);

              NewRelic()?.noticeError(res.error);
              reject(res.error);
              return;
            }
            if (!res.data) {
              reject();
              return;
            }
            resolve(res.data);
          },
        );
      });
    },
  };

  gameConfig = {
    downloadUrl: async ({ fileId }: { fileId: string }) => {
      return new Promise<{ url: string }>((resolve, reject) => {
        this._socket?.emit(
          'game:config:downloadurl',
          { filename: fileId },
          (res: AcknowledgementResponse<{ url: string }>) => {
            if (res.error) {
              console.error(res.error);

              NewRelic()?.noticeError(res.error);
              reject(res.error);
              return;
            }
            if (!res.data) {
              reject();
              return;
            }
            resolve(res.data);
          },
        );
      });
    },
  };

  game = {
    getDownloadUrl: ({ pathname, tenantId }: { pathname: string; tenantId: string }) => {
      return new Promise<{ url: string }>((resolve, reject) => {
        this._socket?.emit(
          'game:downloadurl',
          { pathname, tenantId },
          (res: AcknowledgementResponse<{ url: string }>) => {
            if (res.error) {
              console.error(res.error);

              NewRelic()?.noticeError(res.error);
              reject(res.error);
              return;
            }
            if (!res.data) {
              reject();
              return;
            }
            resolve(res.data);
          },
        );
      });
    },
    getUploadUrl: ({ pathname, tenantId }: { pathname: string; tenantId: string }) => {
      return new Promise<{ url: string }>((resolve, reject) => {
        this._socket?.emit(
          'game:uploadurl',
          { pathname, tenantId },
          (res: AcknowledgementResponse<{ url: string }>) => {
            if (res.error) {
              console.error(res.error);

              NewRelic()?.noticeError(res.error);
              reject(res.error);
              return;
            }
            if (!res.data) {
              reject();
              return;
            }
            resolve(res.data);
          },
        );
      });
    },

    listAllParticipants: ({
      tenantId,
      gameId,
      volatile,
    }: {
      tenantId: string;
      gameId: string;
      volatile: boolean;
    }) => {
      // the initial fetching we dont want to be volatile so we can ensure we get the initial data
      if (volatile) {
        return new Promise<PartialParticipant[]>((resolve, reject) => {
          this._socket?.volatile.emit(
            'game:participants:list',
            { tenantId, gameId },
            (res: AcknowledgementResponse<PartialParticipant[]>) => {
              if (res.error) {
                console.error(res.error);

                NewRelic()?.noticeError(res.error);
                reject(res.error);
                return;
              }
              if (!res.data) {
                reject();
                return;
              }
              resolve(res.data);
            },
          );
        });
      }
      return new Promise<PartialParticipant[]>((resolve, reject) => {
        this._socket?.emit(
          'game:participants:list',
          { tenantId, gameId },
          (res: AcknowledgementResponse<PartialParticipant[]>) => {
            if (res.error) {
              console.error(res.error);

              NewRelic()?.noticeError(res.error);
              reject(res.error);
              return;
            }
            if (!res.data) {
              reject();
              return;
            }
            resolve(res.data);
          },
        );
      });
    },

    listChangedParticipants: ({
      tenantId,
      gameId,
    }: {
      tenantId: string;
      gameId: string;
    }) => {
      return this.ensureConnected().pipe(
        tap(() => {
          this._socket?.emit(
            'game:participants:change',
            { tenantId, gameId },
            (res: AcknowledgementResponse) => {
              console.log('switch');
              if (res.error) {
                console.error(res.error);

                NewRelic()?.noticeError(res.error);
              }
            },
          );
        }),
        switchMap(() =>
          this.createEventObservable<Participant[] | undefined>(
            `tenant/${tenantId}/game/${gameId}/participants:change:partial`,
          ),
        ),
      );
    },
    countParticipants: ({ tenantId, gameId }: { tenantId: string; gameId: string }) => {
      return this.ensureConnected().pipe(
        tap(() => {
          this._socket?.emit(
            'game:participants:count',
            { tenantId, gameId },
            (res: AcknowledgementResponse) => {
              console.log('switch');
              if (res.error) {
                console.error(res.error);

                NewRelic()?.noticeError(res.error);
              }
            },
          );
        }),
        switchMap(() =>
          this.createEventObservable<number | undefined>(
            `tenant/${tenantId}/game/${gameId}/participants:count`,
          ),
        ),
      );
    },
    subscribeVotes: ({
      tenantId,
      gameId,
      slideId,
    }: {
      tenantId: string;
      gameId: string;
      slideId: string;
    }) => {
      return this.ensureConnected().pipe(
        tap(() => {
          this._socket?.emit(
            'game:onvoteschange',
            { tenantId, gameId, slideId },
            (res: AcknowledgementResponse) => {
              if (res.error) {
                console.error(res.error);

                NewRelic()?.noticeError(res.error);
              }
            },
          );
        }),
        switchMap(() =>
          this.createEventObservable<{ count: number }>(
            `tenant/${tenantId}/game/${gameId}/slides/${slideId}/votes:changed`,
          ),
        ),
      );
    },

    save: (game: Game & { modifiedAt: number; tenantId: string }) => {
      // Due to saving consistently we always need modifiedAt to be set
      const parsedGame: GameSaveDTO = {
        id: game.id,
        createdAt: game.createdAt || new Date().getTime(),
        currentRound: game.currentRound,
        host: game.host,
        tenantId: game.tenantId,
        isComplete: game.isComplete,
        isPlaying: game.isPlaying,
        isPreviewGame: game.isPreviewGame,
        lastPlayedAt: game.lastPlayedAt,
        isShowingLobby: game.isShowingLobby !== undefined ? game.isShowingLobby : false,
        modifiedAt: game.modifiedAt,
        pin: null,
        roundStartsAt: game.roundStartsAt,
        roundFreezedAt: game.roundFreezedAt,
        rounds: [], // rounds always empty as its deprecated
        slides: game.slides || [], // now only saves slides
        areAllRoundsCompleted:
          game.areAllRoundsCompleted !== undefined && game.areAllRoundsCompleted !== null
            ? game.areAllRoundsCompleted
            : false,
        isFromDiscover: game.isFromDiscover || null,
        language: game.language || null,
        metadata: game.metadata || null,
        requireRealNames:
          game.requireRealNames === undefined ? true : game.requireRealNames,
        isContentFilterEnabled:
          game.isContentFilterEnabled === undefined ? true : game.isContentFilterEnabled,
        templateId: game.templateId || null,
        title: game.title || null,
        attachments: game.attachments,
      };

      return new Promise<{
        status: number;
      }>((resolve, reject) => {
        /* 
          We use a long timeout to prevent
          events getting lost after socketIO 
          thinks we sent it, but lost connection 1ms later.
        */
        this._socket?.timeout(30000).emit(
          'game:save',
          parsedGame,
          (
            timeoutError: Error,
            res: AcknowledgementResponse<{
              status: number;
            }>,
          ) => {
            if (res && res.error) {
              newRelicNoticeError(res.error, 'game:save');
              reject(res.error);
              return;
            }
            if (timeoutError) {
              NewRelic()?.noticeError(timeoutError);
              reject(timeoutError);
            }
            resolve(res);
          },
        );
      });
    },

    assessAiAnswers: ({
      gameId,
      tenantId,
      slideId,
    }: {
      slideId: string;
      gameId: string;
      tenantId: string;
    }) => {
      return new Promise<{ results: AiAssessedScore[] }>((resolve, reject) => {
        this._socket?.emit(
          'game:ai:feedback',
          { gameId, tenantId, slideId },
          (res: AcknowledgementResponse<{ results: AiAssessedScore[] }>) => {
            if (res.error) {
              console.error(res.error);
              NewRelic()?.noticeError(res.error);
              reject(res.error);
              return;
            }
            if (!res.data) {
              reject();
              return;
            }
            resolve(res.data);
          },
        );
      });
    },

    aiAssessSingle: ({
      gameId,
      tenantId,
      slideId,
      responseIds,
    }: {
      slideId: string;
      gameId: string;
      tenantId: string;
      responseIds: string[];
    }) => {
      return new Promise<{ results: AiAssessedScore[] }>((resolve, reject) => {
        this._socket
          ?.timeout(30000)
          .emit(
            'game:ai:feedback:single',
            { gameId, tenantId, slideId, responseIds },
            (_: Error, res: AcknowledgementResponse<{ results: AiAssessedScore[] }>) => {
              if (res.error) {
                console.error(res.error);
                NewRelic()?.noticeError(res.error);
                reject(res.error);
                return;
              }
              if (!res.data) {
                reject();
                return;
              }
              resolve(res.data);
            },
          );
      });
    },
    aiAssessEditFeedback: ({
      gameId,
      tenantId,
      slideId,
      responseId,
      editedFeedback,
    }: {
      slideId: string;
      gameId: string;
      tenantId: string;
      responseId: string;
      editedFeedback: string;
    }) => {
      return new Promise<{ result: AiAssessedScore[] }>((resolve, reject) => {
        this._socket
          ?.timeout(30000)
          .emit(
            'game:ai:feedback:edit',
            { gameId, tenantId, slideId, responseId, editedFeedback },
            (_: Error, res: AcknowledgementResponse<{ result: AiAssessedScore[] }>) => {
              if (res.error) {
                console.error(res.error);
                NewRelic()?.noticeError(res.error);
                reject(res.error);
                return;
              }
              if (!res.data) {
                reject();
                return;
              }
              resolve(res.data);
            },
          );
      });
    },
    listAllAiAssessments: ({
      gameId,
      tenantId,
      slideId,
    }: {
      slideId: string;
      gameId: string;
      tenantId: string;
    }) => {
      return new Promise<{ results: AiAssessedScore[] }>((resolve, reject) => {
        this._socket?.emit(
          'game:listAiAssessments',
          { gameId, tenantId, slideId },
          (res: AcknowledgementResponse<{ results: AiAssessedScore[] }>) => {
            if (res.error) {
              console.error(res.error);
              NewRelic()?.noticeError(res.error);
              reject(res.error);
              return;
            }
            if (!res.data) {
              reject();
              return;
            }
            resolve(res.data);
          },
        );
      });
    },

    onChange: ({ gameId, tenantId }: { gameId: string; tenantId: string }) => {
      return this.ensureConnected().pipe(
        tap(() => {
          this._socket?.emit(
            'game:subscribe',
            { tenantId, gameId },
            (res: AcknowledgementResponse) => {
              if (res.error) {
                console.error(res.error);

                NewRelic()?.noticeError(res.error);
              }
            },
          );
        }),
        switchMap(() =>
          this.createEventObservable<Game | undefined>(
            `tenant/${tenantId}/game/${gameId}:changed`,
          ),
        ),
      );
    },
    onRoundResponsesChanged: ({
      gameId,
      tenantId,
      slideId,
    }: {
      slideId: string;
      gameId: string;
      tenantId: string;
    }) => {
      return this.ensureConnected().pipe(
        tap(() => {
          this._socket?.emit(
            'game:onresponseschange',
            { tenantId, gameId, slideId },
            (res: AcknowledgementResponse) => {
              if (res.error) {
                NewRelic()?.noticeError(res.error);
              }
            },
          );
        }),
        switchMap(() =>
          this.createEventObservable<Response[]>(
            `tenant/${tenantId}/game/${gameId}/slides/${slideId}/responses:changed`,
          ),
        ),
      );
    },
    onRoundCompletedByParticipants: ({
      gameId,
      tenantId,
      slideId,
    }: {
      slideId: string;
      gameId: string;
      tenantId: string;
    }) => {
      return this.ensureConnected().pipe(
        tap(() => {
          this._socket?.emit(
            'game:slidecompletedbyparticipants',
            { tenantId, gameId, slideId },
            (res: AcknowledgementResponse) => {
              if (res.error) {
                console.error(res.error);

                NewRelic()?.noticeError(res.error);
              }
            },
          );
        }),
        switchMap(() =>
          this.createEventObservable<
            {
              uid: string;
              timestamp: number;
            }[]
          >(
            `tenant/${tenantId}/game/${gameId}/slides/${slideId}/slidecompletedbyparticipants:changed`,
          ),
        ),
      );
    },
  };
}

export default GameAPI;

type AcknowledgementResponse<T = unknown> = {
  status: number;
  error?: { status: number; message?: string };
  data?: T;
};

export type AiAssessedScore = {
  responseId: string; // The related response's id, also used as this document's id.
  gameId: string;
  tenantId: string;
  uid: string;
  modifiedAt: number;
  // ai props below
  score?: string;
  feedback: string;
};
